/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useContext } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose, bindActionCreators } from 'redux'
import pickby from 'lodash.pickby'
import identity from 'lodash.identity'
import cls from 'classnames'

import { linkClick, collapsibleClick } from '@cred/react-analytics/events'
import Link from '@cred/react-link'
import { Testable } from '@cred/react-testable'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'
import { extractNumber } from '@cred/shared/utils'

import PaidAdvertisement from 'components/paid-advertisement'
import { AdvertiserDisclosure } from 'components/advertiser-disclosure'
import useBreakpointWidth from 'common/hooks/useBreakpointWidth'
import { setSubmission as setSubmissionAction } from 'utils/redux/submission/action'
import { getMetaQuery } from 'utils/data'
import withReduxProvider from 'components/hocs/withReduxProvider'
import withApolloProvider from 'components/hocs/withApolloProvider'
import InitialValuesContext from 'components/initial-values-context'
import CtaButton from 'components/cta-button'
import { ADVERTISER_DISCLOSURE_THEMES } from 'common/constants'

import CompareRates from './compare-rates'
import useProductsAndFormHandler from './shared/hooks/useProductsAndFormHandler'
import SortingForm from './sorting-form'
import TabsForm from './tabs-form'
import RateWidgetFooter from './footer'
import styles from '../themes/default.module.scss'
import {
  PURCHASE_TYPE,
  REFINANCE_TYPE,
  MORTGAGE_FILTER_FORM_NAME,
  CASH_OUT_LIMIT_INDEX,
} from './common/constants'
import {
  generateOnGetStartedCallback,
  getMortFilterText,
  lenderCentricProductsCheck,
  mortgageWidgetMapStateToProps,
  textForMobile,
} from './common/utils'
import FilterFormCombined from './filter-form-combined'
import editIcon from '../../assets/icons/edit-icon.svg'
import { lendersBestSortingDropdown, sortingDropdownMortgageCombined } from './common/options'
import ehlLogo from '../../assets/ehl-logo.svg'
import { containsHostname } from '../../../utils/urls'

const getOrderByOptions = (vertical, tabName) => {
  const config = {
    [PURCHASE_TYPE]: {
      'top-rates': lendersBestSortingDropdown,
      'all-rates': sortingDropdownMortgageCombined,
    },
    [REFINANCE_TYPE]: {
      'top-rates': sortingDropdownMortgageCombined,
      'all-rates': sortingDropdownMortgageCombined,
    },
  }
  return config[vertical]?.[tabName]
}

const getFilteredProducts = (products, sortBy) => {
  if (!Array.isArray(products) || !sortBy) return []
  return [...products].sort((a, b) => a[sortBy] - b[sortBy])
}

const paginationName = 'mortgage-rate-table'
const LENDER_PER_PAGE = 5

const Widget = (props) => {
  const {
    variation,
    trackSnowplowEvent,
    /* eslint-disable-next-line */
    utms: { utm_campaign, utm_source, utm_medium, utm_content },
    initialValues,
    partnerFormData,
    sortBy,
    termState,
    isPurchase,
    theme,
    metaParams,
    verticals,
    actions: { setSubmission },
    widgetContext: {
      parentPage: { partnerURL },
      options: { rateTableConfig: { rateTableCTAUrl } = {} } = {},
    } = {},
    widgetContext,
    analyticsContexts = [],
    omitUtmParams = false,
    page = 0,
  } = props

  initialValues.type = {
    Conventional: true,
  }

  const { activeTab } = useContext(InitialValuesContext) || {}
  const [currentTab, setCurrentTab] = useState(activeTab)
  const [type, setType] = useState(initialValues.type)
  const [showDisclosure] = useState(ADVERTISER_DISCLOSURE_THEMES.includes(theme))
  const metaQuery = getMetaQuery(
    { ...metaParams, marketplace: verticals, type: 'rate-table' },
    theme,
  )

  const [submitFormValues, formValues, currentProducts, loadingProducts, error] =
    useProductsAndFormHandler({
      initialValues,
      partnerFormData,
      setSubmission,
      withLenderCentricProducts: true,
      withBestRefinanceProducts: true,
      type,
    })
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useBreakpointWidth('mobile')

  const isFullTable = variation !== 'shortened'

  const utmParams = pickby(
    {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
    },
    identity,
  )

  const onGetStarted = generateOnGetStartedCallback({
    utmParams,
    isPurchase,
    omitUtmParams,
    metaQuery,
    rateTableCTAUrl,
    widgetContext,
  })
  const { sorting } = initialValues

  const scrollToBottom = () => {
    try {
      const parentWindow = window.parent
      const parentDocumentHeight = parentWindow.document.body.scrollHeight
      parentWindow.scrollTo(0, parentDocumentHeight)
    } catch (err) {
      console.error('Error at scrollToBottom function: ', err)
    }
  }

  const toggleCollapse = () => {
    setIsOpen(!isOpen)
    trackSnowplowEvent(collapsibleClick, {
      text: 'Edit icon',
      action: isOpen ? 'collapse' : 'expand',
    })
  }
  const editButton = (opened) => (
    <Testable className={styles.captureHeader} onClick={toggleCollapse} testId="header-capture">
      {opened ? (
        <p className="secondary500 bodySmall semiBold">Dismiss</p>
      ) : (
        <CtaButton size="small" variant="outlined" className={styles.collapseButton}>
          Edit <img src={editIcon} alt="Edit" />
        </CtaButton>
      )}
    </Testable>
  )

  const { cashOut, homeValue, loanAmount, downPayment, estimatedValue } = formValues || {}
  const isCashOutError =
    !isPurchase && extractNumber(cashOut) / extractNumber(homeValue) >= CASH_OUT_LIMIT_INDEX
  const isValidationsError = isPurchase
    ? extractNumber(downPayment) >= extractNumber(estimatedValue)
    : extractNumber(loanAmount) >= extractNumber(homeValue)
  const isErrorState = isCashOutError || isValidationsError

  const sortingOptions = getOrderByOptions(formValues?.loanType, currentTab)
  const sortingLabel = sortingOptions?.find((option) => option.value === sortBy)?.label
  const filterText = textForMobile(
    getMortFilterText(formValues, termState, isPurchase),
    isMobile,
    sortingLabel,
  )

  const filterFormProps = {
    onUpdateResults: setIsOpen,
    analyticsContexts,
    loadingProducts,
    initialValues: formValues,
    form: MORTGAGE_FILTER_FORM_NAME,
    onSubmit: (v) => {
      return isCashOutError || isValidationsError
        ? null
        : submitFormValues(v, lenderCentricProductsCheck(v?.loanType, currentTab))
    },
    isCashOutError,
    isValidationsError,
    isOpen,
    filterText,
    sortBy,
    isMobile,
    setType,
  }
  const paginationProps = {
    paginationName,
    variation,
    page,
    LENDER_PER_PAGE,
  }

  const sortingForm = (
    <SortingForm
      sortingOptions={sortingOptions}
      initialValues={{ sorting }}
      className={cls(!isMobile ? styles.expInputWrapper : '', styles.verticalDropdown)}
    />
  )

  const hidePoweredBy = containsHostname('credible.com', partnerURL)
  // eslint-disable-next-line camelcase
  const isFoxMoney = theme === 'foxmoney' || utm_source === 'foxmoney'

  return (
    <div className={styles.mainContainer}>
      <div className={styles.disclosuresWrap}>
        {showDisclosure && <AdvertiserDisclosure tooltipId="fox-disclosure" theme={theme} />}
        <PaidAdvertisement display={isFoxMoney} />
      </div>
      <Testable className={styles.container} testId="rate-widget-container">
        <div className={styles.titleWrapper} />
        {loadingProducts && <div className={styles.overlay} />}
        <Testable className={styles.header} testId="rate-widget-header">
          <div className={styles.capture}>
            <FilterFormCombined
              {...filterFormProps}
              sortingForm={sortingForm}
              editButton={editButton(isOpen)}
            />
          </div>
        </Testable>
        <TabsForm
          sortingForm={!isMobile ? sortingForm : null}
          onTabChange={setCurrentTab}
          onGetStarted={onGetStarted}
          error={error}
          loading={loadingProducts}
          bestProducts={getFilteredProducts(currentProducts?.refinanceBestProducts, sortBy)}
          allProducts={
            getFilteredProducts(
              isPurchase
                ? currentProducts?.purchaseBrokerLight
                : currentProducts?.refinanceBrokerLight,
              sortBy,
            ) || []
          }
          purchaseLenderCentricProductsSorted={getFilteredProducts(
            currentProducts?.purchaseLenderCentricProducts,
            sortBy,
          )}
          isFullTable={isFullTable}
          analyticsContexts={analyticsContexts}
          isCashOutError={isCashOutError}
          paginationProps={paginationProps}
          activeTab={activeTab}
          isValidationsError={isValidationsError}
          isPurchase={isPurchase}
        />
        {!isErrorState && (
          <CompareRates onGetStarted={onGetStarted} analyticsContexts={analyticsContexts} />
        )}
        <div className={styles.footerWrap}>
          <div>
            <RateWidgetFooter theme={theme} hidePoweredBy={hidePoweredBy} />
            {isFoxMoney && (
              <p className={cls('disclaimer neutral500', styles.footerDisclosure)}>
                Credible Operations, Inc., NMLS ID# 1681276
                <span className={styles.asterisk} onClick={scrollToBottom}>
                  *
                </span>
                <span> | </span>
                <Link
                  type="disclaimer"
                  analytics={linkClick.analytics()}
                  href="https://www.nmlsconsumeraccess.org/TuringTestPage.aspx?ReturnUrl=/EntityDetails.aspx/COMPANY/1681276"
                  blank
                >
                  NMLS Consumer Access
                </Link>
              </p>
            )}
          </div>
          {isFoxMoney && <img src={ehlLogo} alt="ehl logo" className={styles.ehlLogo} />}
        </div>
      </Testable>
    </div>
  )
}

Widget.propTypes = {
  variation: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  trackSnowplowEvent: PropTypes.func.isRequired,
  analyticsContexts: PropTypes.arrayOf(
    PropTypes.shape({
      schema: PropTypes.string,
      data: PropTypes.object,
    }),
  ),
  utms: PropTypes.object.isRequired,
  partnerFormData: PropTypes.object,
  initialValues: PropTypes.object,
  sortBy: PropTypes.string,
  termState: PropTypes.object,
  isPurchase: PropTypes.bool,
  omitUtmParams: PropTypes.bool,
  page: PropTypes.number,
  metaParams: PropTypes.object,
  verticals: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  type: PropTypes.string,
  actions: PropTypes.shape({
    setSubmission: PropTypes.func.isRequired,
  }).isRequired,
  widgetContext: PropTypes.object.isRequired,
}

const mapDispatchToProps = (dispatch) => ({
  actions: {
    setSubmission: bindActionCreators(setSubmissionAction, dispatch),
  },
})

const App = compose(
  withApolloProvider,
  withReduxProvider,
  withSnowplowContexts,
  connect(mortgageWidgetMapStateToProps, mapDispatchToProps),
)(Widget)

export { Widget }
export default App
